import { useLayoutEffect, useState, useRef, useMemo, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DROPDOWN_KEYS, ROUTE_PATHS } from "../../../common/enums/routing-enums";
import { returnRoutesByKey } from "../../../common/util/Navigation/createMenuConfig";
import { createNavigationConfig } from "../../../common/util/Navigation/createNavigationConfig";
import type { NavigationApiResponse } from "../../../services/MyBizShell/mybizShellAPI.types";
import { useAppDispatch, useAppSelector } from "../../../store";
import { setLocalStorageItem } from "../../../reducers/loginLocalStorage";
import defaultContent from "../../../content/navigation";
import smartNextStepsContent from "../../../content/smartNextSteps";
import defaultCommonContent from "../../../content/common";

const PATHS_TO_IGNORE = ["/", ROUTE_PATHS.PERFORMANCE_DASHBOARD, ROUTE_PATHS.GROUP_MANAGEMENT_DASHBOARD];
const MAX_TRACKED_PAGES = 50; // Prevent localStorage pollution

const ROUTING_GROUPS = [
  DROPDOWN_KEYS.PERFORMANCE,
  DROPDOWN_KEYS.EARLY_INCENTIVES,
  DROPDOWN_KEYS.MY_GROUP,
  DROPDOWN_KEYS.LEADER_GROWTH,
];

type TrackablePage = { path: string; labelKey: string; label: string };

interface DesktopFrequentlyUsedLinksProps {
  navigationData?: NavigationApiResponse;
}

export const DesktopFrequentlyUsedLinks = ({ navigationData }: DesktopFrequentlyUsedLinksProps) => {
  const { t } = useTranslation(["navigation", "smartNextSteps", "common"]);
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { shellConfig, isBootLoading, configuration, user } = useAppSelector((state) => state.boot);
  const { pageViewCounts: storedPageViewCounts } = useAppSelector((state) => state.loginLocalStorage);
  const { sideNavExpanded } = useAppSelector((state) => state.uiState);
  const { menuConfig = [] } = shellConfig;

  const disclaimerSymbol = t("disclaimerSymbol", defaultCommonContent["disclaimerSymbol"], { ns: "common" });

  const showWhosNewIncludesCustomers = configuration?.actionReports?.showWhosNewIncludesCustomers ?? false;

  // Memoize availablePages to prevent expensive recalculation on every render
  const availablePages = useMemo<TrackablePage[]>(() => {
    if (navigationData) {
      const navConfig = createNavigationConfig(navigationData, undefined, user.aff);
      const actionReports = showWhosNewIncludesCustomers
        ? navConfig.groupMgmtActionReports.map((item) =>
            item.id === "whosNew" ? { ...item, contentKey: "whosNewWithCustomers" } : item,
          )
        : navConfig.groupMgmtActionReports;
      return [...navConfig.performanceIncome, ...navConfig.groupMgmtNavItems, ...actionReports]
        .filter((item) => !PATHS_TO_IGNORE.includes(item.path))
        .map((item) => ({ path: item.path, labelKey: item.contentKey ?? item.id, label: item.label }));
    }
    return ROUTING_GROUPS.flatMap((group) => returnRoutesByKey(group, menuConfig))
      .filter((page) => !PATHS_TO_IGNORE.includes(page.path))
      .map((page) => ({
        path: page.path,
        labelKey: page.label,
        label: (defaultContent.linkTitle as Record<string, string>)[page.label] ?? page.label,
      }));
  }, [navigationData, menuConfig, showWhosNewIncludesCustomers, user.aff]);

  const getDefaultPageViews = (): Record<string, number> => {
    const defaults: Record<string, number> = {};
    (configuration?.defaultPageViews || []).forEach((page: string) => {
      const path = ROUTE_PATHS[page as keyof typeof ROUTE_PATHS];
      if (path && !PATHS_TO_IGNORE.includes(path)) {
        defaults[path] = 0;
      }
    });
    return defaults;
  };

  const enforceLRULimit = (counts: Record<string, number>): Record<string, number> => {
    const entries = Object.entries(counts);
    if (entries.length <= MAX_TRACKED_PAGES) {
      return counts;
    }
    // Sort by count descending, keep top MAX_TRACKED_PAGES
    const sorted = entries.sort(([, a], [, b]) => b - a);
    return Object.fromEntries(sorted.slice(0, MAX_TRACKED_PAGES));
  };

  // Memoize getTopPages to avoid recreating on every render
  const getTopPages = useCallback(
    (counts: Record<string, number>): TrackablePage[] => {
      const defaults = getDefaultPageViews();
      const filtered: Record<string, number> = {};

      Object.entries(counts).forEach(([path, count]) => {
        if (availablePages.some((page) => page.path === path)) {
          filtered[path] = count;
        }
      });

      if (Object.keys(filtered).length < 3) {
        for (const path in defaults) {
          if (!filtered[path] && Object.keys(filtered).length < 3) {
            filtered[path] = 0;
          }
        }
      }

      // Sort by count descending, then take top 3
      return Object.entries(filtered)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .reduce<TrackablePage[]>((acc, [path]) => {
          const page = availablePages.find((availablePage) => availablePage.path === path);
          if (page) acc.push(page);
          return acc;
        }, []);
    },
    [availablePages, configuration],
  );

  const [pageViewCounts, setPageViewCounts] = useState<Record<string, number>>(
    () => storedPageViewCounts as Record<string, number>,
  );
  const [topPages, setTopPages] = useState<TrackablePage[]>(() =>
    getTopPages(storedPageViewCounts as Record<string, number>),
  );
  const previousPathRef = useRef<string>("");

  // Track page views without causing infinite loops
  useLayoutEffect(() => {
    if (location.pathname !== previousPathRef.current) {
      const trackedPage = availablePages.find((page) => page.path === location.pathname);
      if (trackedPage) {
        const updated = {
          ...pageViewCounts,
          [location.pathname]: (pageViewCounts[location.pathname] ?? 0) + 1,
        };
        const sorted = Object.fromEntries(Object.entries(updated).sort(([, a], [, b]) => b - a));
        const limited = enforceLRULimit(sorted);

        setPageViewCounts(limited);
        setTopPages(getTopPages(limited));

        try {
          dispatch(setLocalStorageItem({ pageViewCounts: limited }));
        } catch (error) {
          console.error("Failed to persist page view counts to localStorage:", error);
        }

        previousPathRef.current = location.pathname;
      }
    }
  }, [location.pathname, availablePages, dispatch, getTopPages, pageViewCounts]);

  if (isBootLoading) {
    return <div className="loading-pulse" style={{ height: "7.5rem" }} />;
  }

  // Don't render anything if no pages are available
  if (topPages.length === 0) {
    return null;
  }

  return (
    <>
      {topPages.map((page) => (
        <Link
          key={page.path}
          to={{ pathname: page.path }}
          className="SideNavigationPanel__nav-item SideNavigationPanel__nav-item--text SideNavigationPanel__hover-circle"
          aria-label={t(`linkTitle.${page.labelKey}`, page.label, { symbol: disclaimerSymbol })}
        >
          <span>
            {sideNavExpanded
              ? t(`linkTitle.${page.labelKey}`, page.label, { symbol: disclaimerSymbol })
              : t(`smartNextSteps:${page.labelKey}`, {
                  defaultValue: t(
                    `linkTitle.${page.labelKey}`,
                    (smartNextStepsContent as Record<string, string>)[page.labelKey] ?? page.label,
                    { symbol: disclaimerSymbol },
                  ),
                  symbol: disclaimerSymbol,
                })}
          </span>
        </Link>
      ))}
    </>
  );
};
